import { type RefCallback, useCallback, useEffect, useRef } from "react";

// Ported from aspen-marketing's app/hooks/useInvocaNumber. Keep the two in sync.

type InvocaSwapState = "pending" | "ready" | "failed";

interface UseInvocaNumberOptions {
  /**
   * If false, the hook is a no-op.
   * @default true
   */
  enabled?: boolean;
  /**
   * Server-rendered fallback number, used to detect a *real* swap.
   * Defaults to the node's initial text content.
   */
  fallback?: string;
  /**
   * Reveal the fallback and drop the loading treatment if PNAPI never swaps.
   * @default 2000
   */
  timeoutMs?: number;
  /**
   * Drive the `data-invoca-state` swap treatment (pending → ready/failed) that
   * CSS can turn into a loading skeleton. Opt-in; PNAPI still runs without it
   * so the href can be swapped for tracking.
   * @default false
   */
  showSwapTreatment?: boolean;
}

interface UseInvocaNumberReturn {
  /** Ref callback — attach to the DOM element Invoca will mutate. */
  ref: RefCallback<HTMLElement>;
}

const onlyDigits = (value: string | null | undefined) => (value ?? "").replaceAll(/\D/g, "");

/**
 * Ensures Invoca's PNAPI.run() is called after the element mounts so that
 * phone-number swapping occurs, and optionally drives a swap-state treatment via
 * the `data-invoca-state` attribute ("pending" | "ready" | "failed").
 *
 * The hook intentionally avoids React state so re-renders never overwrite the
 * DOM values Invoca has set. The state attribute is written imperatively for
 * the same reason — React doesn't manage it, so it won't remove it on re-render.
 *
 * React only leaves Invoca's mutations alone while the element's text / href
 * stay the same. If the number can change, key the element by it so a new
 * number mounts a fresh node (and re-runs this ref) instead of patching one
 * Invoca has already swapped.
 */
export function useInvocaNumber({
  enabled = true,
  fallback,
  timeoutMs = 2000,
  showSwapTreatment = false,
}: UseInvocaNumberOptions = {}): UseInvocaNumberReturn {
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const observerRef = useRef<MutationObserver | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cleanup = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const ref = useCallback(
    (node: HTMLElement | null) => {
      cleanup();

      if (!node || !enabled) return;

      if (showSwapTreatment) {
        const setState = (state: InvocaSwapState) => node.setAttribute("data-invoca-state", state);

        const fallbackDigits = onlyDigits(fallback ?? node.textContent);
        const isSwapped = () => {
          const digits = onlyDigits(node.textContent);
          return digits.length >= 10 && digits !== fallbackDigits;
        };

        // Invoca may have already swapped before this node mounted
        // (warm tag / client-side route change) — settle immediately.
        if (isSwapped()) {
          setState("ready");
        } else {
          setState("pending");

          // Detect the swap landing. The observer watches the exact mutation
          // Invoca makes (child text node + tel: href).
          const observer = new MutationObserver(() => {
            if (isSwapped()) {
              setState("ready");
              cleanup();
            }
          });
          observer.observe(node, {
            childList: true,
            characterData: true,
            subtree: true,
            attributes: true,
            attributeFilter: ["href"],
          });
          observerRef.current = observer;

          // Safety valve: if PNAPI never responds, reveal the fallback (a
          // working number) instead of leaving the user on the skeleton.
          timeoutRef.current = setTimeout(() => {
            setState("failed");
            cleanup();
          }, timeoutMs);
        }
      }

      // Trigger the swap, polling until the Invoca script (loaded via GTM) is available.
      const runPNAPI = () => {
        if (globalThis.Invoca?.PNAPI) {
          try {
            globalThis.Invoca.PNAPI.run();
            return true;
          } catch {
            return false;
          }
        }
        return false;
      };

      if (!runPNAPI()) {
        pollRef.current = setInterval(() => {
          if (runPNAPI() && pollRef.current) {
            // Stop polling for availability only — keep the observer and
            // timeout alive, since the swap itself hasn't landed yet.
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
        }, 500);
      }
    },
    [enabled, fallback, timeoutMs, showSwapTreatment, cleanup],
  );

  useEffect(() => cleanup, [cleanup]);

  return { ref };
}
